import { useState, useEffect, useCallback } from 'react';
import adminApi from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, Trash2, Ban, Search, RefreshCw, 
  Clock, AlertTriangle, CheckCircle, Globe,
  FileText, Filter, Plus
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminIPManager() {
  // State
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [whitelistedIPs, setWhitelistedIPs] = useState([]);
  const [blockLogs, setBlockLogs] = useState([]);
  const [myIP, setMyIP] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [newBlockIP, setNewBlockIP] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockDuration, setBlockDuration] = useState('permanent');
  const [newWhitelistIP, setNewWhitelistIP] = useState('');
  const [whitelistReason, setWhitelistReason] = useState('');
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Loading states
  const [blocking, setBlocking] = useState(false);
  const [whitelisting, setWhitelisting] = useState(false);

  // Fetch functions
  const fetchBlockedIPs = useCallback(async () => {
    try {
      let url = `/blocked-ips`;
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterType !== 'all') params.append('filter_type', filterType);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await adminApi.get(url);
      setBlockedIPs(response.data);
    } catch (error) {
      toast.error('Failed to load blocked IPs');
    }
  }, [searchTerm, filterType]);

  const fetchWhitelistedIPs = useCallback(async () => {
    try {
      const response = await adminApi.get(`/whitelisted-ips`);
      setWhitelistedIPs(response.data);
    } catch (error) {
      toast.error('Failed to load whitelisted IPs');
    }
  }, []);

  const fetchBlockLogs = useCallback(async () => {
    try {
      const response = await adminApi.get(`/block-logs?limit=100`);
      setBlockLogs(response.data);
    } catch (error) {
      toast.error('Failed to load block logs');
    }
  }, []);

  const fetchMyIP = useCallback(async () => {
    try {
      const response = await adminApi.get(`/my-ip`);
      setMyIP(response.data);
    } catch (error) {
      console.error('Failed to get IP');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchBlockedIPs(),
        fetchWhitelistedIPs(),
        fetchBlockLogs(),
        fetchMyIP()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchBlockedIPs, fetchWhitelistedIPs, fetchBlockLogs, fetchMyIP]);

  // Refresh when search/filter changes
  useEffect(() => {
    fetchBlockedIPs();
  }, [searchTerm, filterType, fetchBlockedIPs]);

  // Actions
  const handleBlockIP = async (e) => {
    e.preventDefault();
    if (!newBlockIP.trim()) {
      toast.error('Please enter an IP address');
      return;
    }

    setBlocking(true);
    try {
      await adminApi.post(`/blocked-ips`, {
        ip_address: newBlockIP.trim(),
        reason: blockReason.trim(),
        duration: blockDuration
      });
      toast.success(`IP ${newBlockIP} has been blocked`);
      setNewBlockIP('');
      setBlockReason('');
      setBlockDuration('permanent');
      fetchBlockedIPs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block IP');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblockIP = async (ip) => {
    if (!window.confirm(`Unblock ${ip}?`)) return;
    
    try {
      await adminApi.delete(`/blocked-ips/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} unblocked`);
      fetchBlockedIPs();
    } catch (error) {
      toast.error('Failed to unblock IP');
    }
  };

  const handleWhitelistIP = async (e) => {
    e.preventDefault();
    if (!newWhitelistIP.trim()) {
      toast.error('Please enter an IP address');
      return;
    }

    setWhitelisting(true);
    try {
      await adminApi.post(`/whitelisted-ips`, {
        ip_address: newWhitelistIP.trim(),
        reason: whitelistReason.trim()
      });
      toast.success(`IP ${newWhitelistIP} has been whitelisted`);
      setNewWhitelistIP('');
      setWhitelistReason('');
      fetchWhitelistedIPs();
      fetchBlockedIPs(); // Refresh in case it was removed from blocked
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to whitelist IP');
    } finally {
      setWhitelisting(false);
    }
  };

  const handleRemoveWhitelist = async (ip) => {
    if (!window.confirm(`Remove ${ip} from whitelist?`)) return;
    
    try {
      await adminApi.delete(`/whitelisted-ips/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} removed from whitelist`);
      fetchWhitelistedIPs();
    } catch (error) {
      toast.error('Failed to remove from whitelist');
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Clear all block logs?')) return;
    
    try {
      await adminApi.delete(`/block-logs`);
      toast.success('Block logs cleared');
      fetchBlockLogs();
    } catch (error) {
      toast.error('Failed to clear logs');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getDurationLabel = (duration) => {
    const labels = {
      '1h': '1 Hour',
      '24h': '24 Hours',
      '7d': '7 Days',
      '30d': '30 Days',
      'permanent': 'Permanent'
    };
    return labels[duration] || duration;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-stone-100">
        <p className="text-stone-500">Loading IP Manager...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Your IP */}
      <div className="bg-white rounded-2xl p-6 border border-stone-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#E85D75]" />
            <h2 className="text-2xl font-serif font-medium text-stone-800">IP Block Manager</h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchBlockedIPs();
              fetchWhitelistedIPs();
              fetchBlockLogs();
              fetchMyIP();
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Current IP Info */}
        {myIP && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-800">Your Current IP:</span>
              <code className="bg-blue-100 px-2 py-1 rounded font-mono text-blue-900">
                {myIP.ip_address}
              </code>
              {myIP.is_whitelisted && (
                <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">Whitelisted</span>
              )}
              {myIP.is_blocked && (
                <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full">Blocked</span>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-red-50 p-4 rounded-xl text-center">
            <p className="text-2xl font-bold text-red-600">{blockedIPs.length}</p>
            <p className="text-sm text-red-700">Blocked IPs</p>
          </div>
          <div className="bg-green-50 p-4 rounded-xl text-center">
            <p className="text-2xl font-bold text-green-600">{whitelistedIPs.length}</p>
            <p className="text-sm text-green-700">Whitelisted IPs</p>
          </div>
          <div className="bg-amber-50 p-4 rounded-xl text-center">
            <p className="text-2xl font-bold text-amber-600">{blockLogs.length}</p>
            <p className="text-sm text-amber-700">Blocked Requests</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="blocked" className="w-full">
        <TabsList className="bg-white border border-stone-200 w-full justify-start">
          <TabsTrigger value="blocked" className="flex items-center gap-2">
            <Ban className="w-4 h-4" /> Blocked IPs
          </TabsTrigger>
          <TabsTrigger value="whitelist" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Whitelist
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Block Logs
          </TabsTrigger>
        </TabsList>

        {/* Blocked IPs Tab */}
        <TabsContent value="blocked">
          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            {/* Add New Block Form */}
            <form onSubmit={handleBlockIP} className="mb-6 p-4 bg-stone-50 rounded-xl">
              <h3 className="font-medium text-stone-800 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Block New IP Address
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input
                  type="text"
                  placeholder="IP Address (IPv4 or IPv6)"
                  value={newBlockIP}
                  onChange={(e) => setNewBlockIP(e.target.value)}
                  data-testid="block-ip-input"
                />
                <Input
                  type="text"
                  placeholder="Reason (optional)"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  data-testid="block-reason-input"
                />
                <Select value={blockDuration} onValueChange={setBlockDuration}>
                  <SelectTrigger data-testid="block-duration-select">
                    <SelectValue placeholder="Duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1h">1 Hour</SelectItem>
                    <SelectItem value="24h">24 Hours</SelectItem>
                    <SelectItem value="7d">7 Days</SelectItem>
                    <SelectItem value="30d">30 Days</SelectItem>
                    <SelectItem value="permanent">Permanent</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="submit"
                  disabled={blocking}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  {blocking ? 'Blocking...' : 'Block IP'}
                </Button>
              </div>
            </form>

            {/* Search and Filter */}
            <div className="flex gap-3 mb-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Search IP addresses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-ip-input"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="permanent">Permanent</SelectItem>
                  <SelectItem value="temporary">Temporary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Blocked IPs List */}
            <div className="space-y-2">
              {blockedIPs.length === 0 ? (
                <p className="text-stone-500 text-center py-8">No blocked IPs found</p>
              ) : (
                blockedIPs.map((item) => (
                  <div
                    key={item.ip_address}
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                      item.is_expired 
                        ? 'bg-gray-50 border-gray-200' 
                        : 'bg-red-50 border-red-100'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <code className="font-mono font-medium text-stone-800">
                          {item.ip_address}
                        </code>
                        {item.is_expired && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                            Expired
                          </span>
                        )}
                        {item.is_permanent ? (
                          <span className="text-xs bg-red-200 text-red-700 px-2 py-0.5 rounded flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Permanent
                          </span>
                        ) : (
                          <span className="text-xs bg-amber-200 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {getDurationLabel(item.duration)}
                          </span>
                        )}
                        {item.block_count > 0 && (
                          <span className="text-xs bg-stone-200 text-stone-600 px-2 py-0.5 rounded">
                            {item.block_count} attempts blocked
                          </span>
                        )}
                      </div>
                      {item.reason && (
                        <p className="text-sm text-stone-600 mt-1">Reason: {item.reason}</p>
                      )}
                      <p className="text-xs text-stone-500 mt-1">
                        Blocked: {formatDate(item.blocked_at)}
                        {item.expires_at && ` • Expires: ${formatDate(item.expires_at)}`}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnblockIP(item.ip_address)}
                      className="text-stone-600 hover:text-stone-800"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Unblock
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Whitelist Tab */}
        <TabsContent value="whitelist">
          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            {/* Add to Whitelist Form */}
            <form onSubmit={handleWhitelistIP} className="mb-6 p-4 bg-green-50 rounded-xl">
              <h3 className="font-medium text-stone-800 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add to Whitelist
              </h3>
              <p className="text-sm text-stone-600 mb-3">
                Whitelisted IPs will never be blocked, even if they appear in the blocked list.
              </p>
              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder="IP Address (IPv4 or IPv6)"
                  value={newWhitelistIP}
                  onChange={(e) => setNewWhitelistIP(e.target.value)}
                  className="flex-1"
                  data-testid="whitelist-ip-input"
                />
                <Input
                  type="text"
                  placeholder="Reason (optional)"
                  value={whitelistReason}
                  onChange={(e) => setWhitelistReason(e.target.value)}
                  className="flex-1"
                  data-testid="whitelist-reason-input"
                />
                <Button
                  type="submit"
                  disabled={whitelisting}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {whitelisting ? 'Adding...' : 'Whitelist'}
                </Button>
              </div>
            </form>

            {/* Whitelisted IPs List */}
            <div className="space-y-2">
              {whitelistedIPs.length === 0 ? (
                <p className="text-stone-500 text-center py-8">No whitelisted IPs</p>
              ) : (
                whitelistedIPs.map((item) => (
                  <div
                    key={item.ip_address}
                    className="flex items-center justify-between p-4 bg-green-50 border border-green-100 rounded-xl"
                  >
                    <div>
                      <code className="font-mono font-medium text-stone-800">
                        {item.ip_address}
                      </code>
                      {item.reason && (
                        <p className="text-sm text-stone-600 mt-1">{item.reason}</p>
                      )}
                      <p className="text-xs text-stone-500 mt-1">
                        Added: {formatDate(item.added_at)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveWhitelist(item.ip_address)}
                      className="text-stone-600 hover:text-stone-800"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Block Logs Tab */}
        <TabsContent value="logs">
          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-stone-800">Blocked Request Logs</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearLogs}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Logs
              </Button>
            </div>

            {blockLogs.length === 0 ? (
              <p className="text-stone-500 text-center py-8">No blocked requests logged</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-3 px-2 font-semibold text-stone-600">IP Address</th>
                      <th className="text-left py-3 px-2 font-semibold text-stone-600">Date/Time</th>
                      <th className="text-left py-3 px-2 font-semibold text-stone-600">URL</th>
                      <th className="text-left py-3 px-2 font-semibold text-stone-600">User Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockLogs.map((log, idx) => (
                      <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-3 px-2">
                          <code className="font-mono text-red-700">{log.ip_address}</code>
                        </td>
                        <td className="py-3 px-2 text-stone-600">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="py-3 px-2 text-stone-600 max-w-xs truncate">
                          {log.url}
                        </td>
                        <td className="py-3 px-2 text-stone-500 text-xs max-w-xs truncate">
                          {log.user_agent}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
